import React from 'react'
import {useTranslation} from 'react-i18next'
import {useAppState} from '../../../state'
import {LabelInput} from '../../Atoms/Forms'
import {Baseline, Row} from '../../Layout/Grid'
import {P, H4, SmallUnit} from '../../Atoms/Typography'
import styled from 'styled-components'
import {IconButton, TextButton} from '../../../Components/Atoms/Buttons'
import {IconDelete} from '../../../Assets/Icons/TinyIcons'

const CoolerTypeListContainer = styled.div`
  padding-top: 2rem;
`

const CoolerTypeList = styled.ul``

const CoolerTypeItem = styled.li`
  position: relative;
  width: 100%;
  padding-bottom: 1.25rem;
  list-style: none;

  :not(:first-child) {
    padding-top: 1.25rem;
  }
`

const CoolerTypeName = styled.div`
  margin-top: 1rem;
  font-family: 'HelveticaNeue-Medium', 'Helvetica Neue Medium', Helvetica, Arial, 'FreeSans', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
`

interface Props {
  controller: any
}

const EditCoolerTypes = ({controller: fc}: Props) => {
  const {t} = useTranslation(['appliances', 'tasks'])
  const {site} = useAppState().state

  return (
    <CoolerTypeListContainer>
      <Baseline>
        <H4>{t('appliances:labels.alarmLimitsTitle', 'Alarm limits')}</H4>

        <CoolerTypeList>
          {fc.fields && fc.fields.length > 0 ? (
            fc.fields.map((coolerType: any, index: number) => {
              const markedForDelete = !!fc.fields[`${index}`]?.markedForDelete
              if (fc.isRemoved(fc.fields[index].coolerTypeId)) return null
              const inUse = !!fc.fields[`${index}`]?.inUse
              const hasSlow = coolerType.cycleType === 'SLOW' || coolerType.cycleType === 'SLOW-FAST'
              const hasFast = coolerType.cycleType === 'FAST' || coolerType.cycleType === 'SLOW-FAST'

              const timeLimits: Array<number | undefined> = []
              if (hasSlow) timeLimits.push(coolerType.slowCycleTimeLimit)
              if (hasFast) timeLimits.push(coolerType.fastCycleTimeLimit)

              return (
                <CoolerTypeItem key={fc.fields[index].coolerTypeId}>
                  <Baseline margin={'1rem'}>
                    <CoolerTypeName>
                      {t('appliances:equipmentTypes.cooler', 'Cooler') +
                        ` (${timeLimits.filter(Boolean).join(', ')} min)`}
                    </CoolerTypeName>
                    <input
                      type="hidden"
                      ref={fc.register()}
                      name={`coolerTypes.${index}.coolerTypeId`}
                      defaultValue={coolerType.coolerTypeId}
                    />
                    <input
                      type="hidden"
                      ref={fc.register()}
                      name={`coolerTypes.${index}.cycleType`}
                      defaultValue={coolerType.cycleType}
                    />

                    {hasSlow && (
                      <Row childMargin="1rem" align="flex-end">
                        <LabelInput
                          required
                          type="number"
                          ref={fc.register({required: true})}
                          name={`coolerTypes.${index}.slowCycleStartValue`}
                          defaultValue={coolerType.slowCycleStartValue}
                          onBlur={fc.makeBlur(index, 'slowCycleStartValue')}
                          errorMsg={
                            fc.errors?.coolerTypes?.[`${index}`]?.slowCycleStartValue
                              ? t('common:validation.invalidValue', 'Invalid or missing value')
                              : undefined
                          }
                          labelText={t('appliances:labels.coolerStartTemperature', 'Start temp.')}
                        />

                        <LabelInput
                          required
                          type="number"
                          ref={fc.register({required: true})}
                          name={`coolerTypes.${index}.slowCycleTargetValue`}
                          defaultValue={coolerType.slowCycleTargetValue}
                          onBlur={fc.makeBlur(index, 'slowCycleTargetValue')}
                          errorMsg={
                            fc.errors?.coolerTypes?.[`${index}`]?.slowCycleTargetValue
                              ? t('common:validation.invalidValue', 'Invalid or missing value')
                              : undefined
                          }
                          labelText={t('appliances:labels.coolerTargetTemperature', 'Target temp.')}
                        />

                        <SmallUnit>°{site?.temperatureUnit}</SmallUnit>

                        <LabelInput
                          required
                          type="number"
                          ref={fc.register({required: true})}
                          name={`coolerTypes.${index}.slowCycleTimeLimit`}
                          defaultValue={coolerType.slowCycleTimeLimit}
                          onBlur={fc.makeBlur(index, 'slowCycleTimeLimit')}
                          errorMsg={
                            fc.errors?.coolerTypes?.[`${index}`]?.slowCycleTimeLimit
                              ? t('common:validation.invalidValue', 'Invalid or missing value')
                              : undefined
                          }
                          labelText={t('tasks:labels.duration', 'Duration')}
                        />
                        <SmallUnit>min</SmallUnit>
                      </Row>
                    )}

                    {hasFast && (
                      <Row childMargin="1rem" align="flex-end">
                        <LabelInput
                          required
                          ref={fc.register({required: true})}
                          type="number"
                          name={`coolerTypes.${index}.fastCycleStartValue`}
                          defaultValue={coolerType.fastCycleStartValue}
                          onBlur={fc.makeBlur(index, 'fastCycleStartValue')}
                          errorMsg={
                            fc.errors?.coolerTypes?.[`${index}`]?.fastCycleStartValue
                              ? t('common:validation.invalidValue', 'Invalid or missing value')
                              : undefined
                          }
                          labelText={t('appliances:labels.coolerStartTemperature', 'Start temp.')}
                        />

                        <LabelInput
                          required
                          type="number"
                          ref={fc.register({required: true})}
                          name={`coolerTypes.${index}.fastCycleTargetValue`}
                          defaultValue={coolerType.fastCycleTargetValue}
                          onBlur={fc.makeBlur(index, 'fastCycleTargetValue')}
                          errorMsg={
                            fc.errors?.coolerTypes?.[`${index}`]?.fastCycleTargetValue
                              ? t('common:validation.invalidValue', 'Invalid or missing value')
                              : undefined
                          }
                          labelText={t('appliances:labels.coolerTargetTemperature', 'Target temp.')}
                        />

                        <SmallUnit>°{site?.temperatureUnit}</SmallUnit>

                        <LabelInput
                          required
                          type="number"
                          ref={fc.register({required: true})}
                          name={`coolerTypes.${index}.fastCycleTimeLimit`}
                          defaultValue={coolerType.fastCycleTimeLimit}
                          onBlur={fc.makeBlur(index, 'fastCycleTimeLimit')}
                          errorMsg={
                            fc.errors?.coolerTypes?.[`${index}`]?.fastCycleTimeLimit
                              ? t('common:validation.invalidValue', 'Invalid or missing value')
                              : undefined
                          }
                          labelText={t('tasks:labels.duration', 'Duration')}
                        />
                        <SmallUnit>min</SmallUnit>
                      </Row>
                    )}

                    <div style={{opacity: inUse ? 0.4 : 1}}>
                      <IconButton
                        buttonProps={{
                          onClick: () => fc.handleRemove(index),
                          disabled: inUse
                        }}
                        icon={<IconDelete />}
                        description={t('common:actions.delete', 'Delete')}
                      />
                    </div>

                    {inUse && markedForDelete ? (
                      <div style={{color: 'red'}}>
                        {t('appliances:messages.inUse', 'Storage type has equipments, and thus cannot be deleted')}
                      </div>
                    ) : null}
                  </Baseline>
                </CoolerTypeItem>
              )
            })
          ) : (
            <P>{t('appliances:messages.noAlarmLimits', 'No defined alarm limits.')}</P>
          )}
        </CoolerTypeList>

        <Row>
          <TextButton onClick={() => fc.handleAdd()}>{t('common:actions.addNew', 'Add new')}</TextButton>
        </Row>
      </Baseline>
    </CoolerTypeListContainer>
  )
}

export default EditCoolerTypes
